export const FOOTBALL_PACKAGE_ID = '7574192';
export const FOOTBALL_PRODUCT_PATH = `/product/${FOOTBALL_PACKAGE_ID}`;

export const GEM_PACKAGES = Object.freeze([
  Object.freeze({ packageId: '7574214', gems: 50 }),
  Object.freeze({ packageId: '7574216', gems: 100 }),
  Object.freeze({ packageId: '7574221', gems: 250 }),
  Object.freeze({ packageId: '7574225', gems: 500 }),
  Object.freeze({ packageId: '7574229', gems: 1000 })
]);

export const GEM_PACKAGE_IDS = Object.freeze(
  GEM_PACKAGES.map(({ packageId }) => packageId)
);

export const RETRO_FOOTBALL_PACKAGE_IDS = Object.freeze([
  FOOTBALL_PACKAGE_ID,
  ...GEM_PACKAGE_IDS
]);

const GEM_PACKAGE_ID_SET = new Set(GEM_PACKAGE_IDS);
const RETRO_FOOTBALL_PACKAGE_ID_SET = new Set(RETRO_FOOTBALL_PACKAGE_IDS);

export const getPackageId = (item) => String(
  item?.tebexPackageId ?? item?.packageId ?? item?.id ?? ''
);

export const isFootballPackage = (itemOrId) => {
  const packageId = typeof itemOrId === 'object'
    ? getPackageId(itemOrId)
    : String(itemOrId ?? '');
  return packageId === FOOTBALL_PACKAGE_ID;
};

export const isGemPackage = (itemOrId) => {
  const packageId = typeof itemOrId === 'object'
    ? getPackageId(itemOrId)
    : String(itemOrId ?? '');
  return GEM_PACKAGE_ID_SET.has(packageId);
};

export const isRetroFootballPackage = (itemOrId) => {
  const packageId = typeof itemOrId === 'object'
    ? getPackageId(itemOrId)
    : String(itemOrId ?? '');
  return RETRO_FOOTBALL_PACKAGE_ID_SET.has(packageId);
};

export const getGemAmount = (itemOrId) => {
  const packageId = typeof itemOrId === 'object'
    ? getPackageId(itemOrId)
    : String(itemOrId ?? '');
  return GEM_PACKAGES.find((entry) => entry.packageId === packageId)?.gems || 0;
};

const invalid = (code, message) => ({ valid: false, code, message });

/**
 * Validate ownership rules that must also be enforced by the fulfillment Worker.
 * Generic products are deliberately ignored.
 */
export const validateRetroFootballCart = (items = []) => {
  const normalizedItems = Array.isArray(items) ? items : [];
  const footballRows = normalizedItems.filter(isFootballPackage);
  const gemRows = normalizedItems.filter(isGemPackage);

  if (footballRows.length > 1) {
    return invalid(
      'football_duplicate',
      'Retro Fantasy Football can only appear once in a checkout.'
    );
  }

  if (footballRows.some((item) => Number(item.quantity ?? item.qty ?? 1) !== 1)) {
    return invalid(
      'football_quantity',
      'Retro Fantasy Football quantity must be exactly one.'
    );
  }

  if (gemRows.some((item) => item.giftTo || item.targetUsername)) {
    return invalid(
      'gem_gift',
      'Fantasy Gems cannot be gifted.'
    );
  }

  if (gemRows.some((item) => {
    const quantity = Number(item.quantity ?? item.qty ?? 1);
    return !Number.isInteger(quantity) || quantity < 1;
  })) {
    return invalid(
      'gem_quantity',
      'Fantasy Gem quantity must be a positive whole number.'
    );
  }

  const giftFootball = footballRows.find(
    (item) => item.giftTo || item.targetUsername
  );
  if (giftFootball && gemRows.length > 0) {
    return invalid(
      'gift_football_with_gems',
      'A gifted Retro Fantasy Football license and Fantasy Gems need separate checkouts.'
    );
  }

  return {
    valid: true,
    code: null,
    message: null,
    hasIap: footballRows.length > 0 || gemRows.length > 0,
    hasFootball: footballRows.length > 0,
    hasGems: gemRows.length > 0,
    isGiftFootball: Boolean(giftFootball),
    giftRecipient: giftFootball?.giftTo || giftFootball?.targetUsername || null
  };
};

export const validateRetroFootballCartAddition = (items = [], product = {}) => {
  if (!isRetroFootballPackage(product)) {
    return { valid: true, code: null, message: null };
  }

  const existingItems = Array.isArray(items) ? items : [];

  if (isGemPackage(product) && product.giftTo) {
    return invalid('gem_gift', 'Fantasy Gems cannot be gifted.');
  }

  if (
    isFootballPackage(product) &&
    existingItems.some(isFootballPackage)
  ) {
    return invalid(
      'football_duplicate',
      'Retro Fantasy Football is already in your cart.'
    );
  }

  return validateRetroFootballCart([
    ...existingItems,
    {
      ...product,
      quantity: isFootballPackage(product) ? 1 : (product.quantity || 1)
    }
  ]);
};
